// src/components/AGTFit/goals/FitGoals.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Fab,
  useTheme 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus } from 'lucide-react';
import agtfitService from '../service/agtfitService';
import { debounce } from 'lodash';

// Importar los componentes separados
import CheckIns from './CheckIns';
import Goals from './Goals';
import Calories from './Calories';
import Weight from './Weight';

// Color principal (Unused constant)
// const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Separador centrado con ancho limitado
const SectionDivider = styled(Box)(({ theme }) => ({
  width: '40%',
  height: '1px',
  margin: '40px auto',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
}));

// Botón flotante para Check-In
const CheckInFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(11),
  right: theme.spacing(3),
  background: 'linear-gradient(135deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#E65100',
  },
  boxShadow: '0 4px 12px rgba(255, 107, 0, 0.4)',
}));

const FitGoals = ({ onGoalUpdated }) => {
  const theme = useTheme();
  
  // Estados para datos dinámicos
  const [checkIns, setCheckIns] = useState([]);
  const [goalData, setGoalData] = useState(null);
  const [caloriesData, setCaloriesData] = useState([]);
  const [weightData, setWeightData] = useState([]);
  const [loading, setLoading] = useState({
    checkIns: true,
    goal: true,
    caloriesStats: true,
    weightStats: true
  });

  // Crear funciones con debounce para cambios de período
  const debouncedLoadStats = useRef(
    debounce(async (type, period) => {
      await loadStats(type, period);
    }, 300) // 300ms de debounce
  ).current;

  // Efecto para cargar datos iniciales
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchInitialData();

    // Limpiar debounce al desmontar
    return () => {
      debouncedLoadStats.cancel();
    };
  }, [debouncedLoadStats, fetchInitialData]);

  // Función para cargar datos iniciales
  const fetchInitialData = async () => {
    // Cargar check-ins primero para tener el peso actual
    setLoading(prev => ({ ...prev, checkIns: true }));
    const checkInsResponse = await agtfitService.getCheckIns();
    if (checkInsResponse.success) {
      setCheckIns(checkInsResponse.data);
    }
    setLoading(prev => ({ ...prev, checkIns: false }));
    
    // Luego cargar objetivo (que usará el peso actual de los check-ins)
    fetchGoalData();
    
    // Cargar estadísticas iniciales de forma progresiva para no saturar
    setTimeout(() => {
      loadStats('calories', 'week');
      
      // Cargar datos de peso después de un pequeño retraso
      setTimeout(() => {
        loadStats('weight', 'week');
      }, 1000);
    }, 500);
  };
  
  // Función para obtener datos del objetivo
  const fetchGoalData = async () => {
    setLoading(prev => ({ ...prev, goal: true }));
    const goalResponse = await agtfitService.getGoal();
    if (goalResponse.success) {
      setGoalData(goalResponse.data);
    }
    setLoading(prev => ({ ...prev, goal: false }));
  };
  
  // Función para cargar estadísticas según tipo y período
  const loadStats = async (type, period) => {
    const statType = type === 'calories' ? 'calories' : 'weight';
  
    setLoading(prev => ({ ...prev, [`${type}Stats`]: true }));
  
    if (statType === 'calories') {
      try {
        // Usar directamente los datos de las comidas para las estadísticas de calorías
        const today = new Date(); // Usar la fecha específica para pruebas
        const todayStr = today.toISOString().split('T')[0];
  
        // Calcular la fecha de inicio según el período (Unused variable)
        // let startDate = todayStr;
        // if (period === 'week' || period === '7D') {
        //   const weekStart = new Date(today);
        //   weekStart.setDate(today.getDate() - 6); // 6 días atrás
        //   startDate = weekStart.toISOString().split('T')[0];
        // }
  
        // Obtener todas las comidas para procesarlas según el período
        const mealsResponse = await agtfitService.getMeals(todayStr);
  
        if (mealsResponse.success && Array.isArray(mealsResponse.data)) {
  
          // Procesar los datos de comidas para el formato del gráfico
          const formattedData = formatMealsData(mealsResponse.data, period);
  
          setCaloriesData(formattedData.length > 0 ? formattedData : generateDefaultCaloriesData(period));
        } else {
          // Datos por defecto si no hay comidas
          setCaloriesData(generateDefaultCaloriesData(period));
        }
      } catch (error) {
        console.error("Error al cargar estadísticas de calorías:", error);
        setCaloriesData(generateDefaultCaloriesData(period));
      }
    } else {
      const statsResponse = await agtfitService.getProgressStats('weight', period);
      if (statsResponse.success && statsResponse.data.length > 0) {
        // Formatear datos para el gráfico
        const formattedData = formatWeightData(statsResponse.data, period);
        setWeightData(formattedData);
      } else {
        // Usar datos de ejemplo si falla la carga
        setWeightData(generateDefaultWeightData(period));
      }
    }
  
    setLoading(prev => ({ ...prev, [`${type}Stats`]: false }));
  };

  // Función para formatear datos de comidas para el gráfico de calorías
  const formatMealsData = (meals, period) => {
    if (!Array.isArray(meals) || meals.length === 0) {
      return [];
    }
    
    // Usar una fecha fija para pruebas
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Para la semana, mostrar los últimos 7 días
    if (period === 'week' || period === '7D') {
      // Generar array de los últimos 7 días
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        last7Days.push({
          date: dateStr,
          label: getDayLabel(dateStr),
          dayLabel: getDayLabel(dateStr),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: dateStr === todayStr
        });
      }
      
      // Calcular totales para cada día usando los datos de comidas
      meals.forEach(meal => {
        const mealDate = meal.mealDate || meal.date;
        
        if (mealDate) {
          const dayData = last7Days.find(day => day.date === mealDate);
          if (dayData) {
            // Sumar los valores nutricionales
            dayData.calories += parseFloat(meal.calories) || 0;
            dayData.protein += parseFloat(meal.protein) || 0;
            dayData.carbs += parseFloat(meal.carbs) || 0;
            dayData.fats += parseFloat(meal.fats) || 0;
            dayData.fiber += parseFloat(meal.fiber) || 0;
            dayData.sugar += parseFloat(meal.sugar) || 0;
            dayData.sodium += parseFloat(meal.sodium) || 0;
          } else {
          }
        }
      });
      
      return last7Days;
    } else if (period === 'month' || period === '1M') {
      // Para mes, mostrar por semanas (4 semanas)
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      // Obtener el primer día del mes
      const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
      
      // Generar array para 4 semanas
      const weeks = [];
      for (let week = 1; week <= 4; week++) {
        const weekStart = new Date(firstDayOfMonth);
        weekStart.setDate((week - 1) * 7 + 1);
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        // Si estamos en la semana actual
        const isCurrentWeek = today >= weekStart && today <= weekEnd;
        
        weeks.push({
          date: weekStart.toISOString().split('T')[0],
          endDate: weekEnd.toISOString().split('T')[0],
          label: `W${week}`,
          dayLabel: `Week ${week}`,
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: isCurrentWeek,
          mealCount: 0 // Para calcular promedios
        });
      }
      
      // Filtrar comidas del mes actual
      const currentMonthMeals = meals.filter(meal => {
        const mealDate = meal.mealDate || meal.date;
        if (!mealDate) return false;
        
        const mealMonth = new Date(mealDate).getMonth();
        const mealYear = new Date(mealDate).getFullYear();
        return mealMonth === currentMonth && mealYear === currentYear;
      });
      
      // Asignar cada comida a su semana correspondiente y calcular sumas
      currentMonthMeals.forEach(meal => {
        const mealDate = meal.mealDate || meal.date;
        if (mealDate) {
          const mealDateObj = new Date(mealDate);
          
          // Encontrar la semana a la que pertenece esta comida
          const weekData = weeks.find(week => {
            const weekStart = new Date(week.date);
            const weekEnd = new Date(week.endDate);
            return mealDateObj >= weekStart && mealDateObj <= weekEnd;
          });
          
          if (weekData) {
            // Sumar los valores nutricionales
            weekData.calories += parseFloat(meal.calories) || 0;
            weekData.protein += parseFloat(meal.protein) || 0;
            weekData.carbs += parseFloat(meal.carbs) || 0;
            weekData.fats += parseFloat(meal.fats) || 0;
            weekData.fiber += parseFloat(meal.fiber) || 0;
            weekData.sugar += parseFloat(meal.sugar) || 0;
            weekData.sodium += parseFloat(meal.sodium) || 0;
            weekData.mealCount++;
          }
        }
      });
      
      // Convertir sumas en promedios para el mes
      weeks.forEach(week => {
        if (week.mealCount > 0) {
          week.calories = Math.round(week.calories / week.mealCount);
          week.protein = Math.round(week.protein / week.mealCount);
          week.carbs = Math.round(week.carbs / week.mealCount);
          week.fats = Math.round(week.fats / week.mealCount);
          week.fiber = Math.round(week.fiber / week.mealCount);
          week.sugar = Math.round(week.sugar / week.mealCount);
          week.sodium = Math.round(week.sodium / week.mealCount);
        }
      });
      
      return weeks;
    } else if (period === '3month' || period === '3M') {
      // Para 3 meses, mostrar los últimos 3 meses
      const months = [];
      
      for (let i = 2; i >= 0; i--) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const nextMonth = new Date(today.getFullYear(), today.getMonth() - i + 1, 0);
        
        months.push({
          date: monthDate.toISOString().split('T')[0],
          endDate: nextMonth.toISOString().split('T')[0],
          label: monthDate.toLocaleDateString('en-US', { month: 'short' }),
          dayLabel: monthDate.toLocaleDateString('en-US', { month: 'long' }),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: i === 0,
          mealCount: 0
        });
      }
      
      // Asignar cada comida a su mes correspondiente
      meals.forEach(meal => {
        const mealDate = meal.mealDate || meal.date;
        if (mealDate) {
          const mealDateObj = new Date(mealDate);
          
          // Encontrar el mes al que pertenece esta comida
          const monthData = months.find(month => {
            const monthStart = new Date(month.date);
            const monthEnd = new Date(month.endDate);
            return mealDateObj >= monthStart && mealDateObj <= monthEnd;
          });
          
          if (monthData) {
            // Sumar los valores nutricionales
            monthData.calories += parseFloat(meal.calories) || 0;
            monthData.protein += parseFloat(meal.protein) || 0;
            monthData.carbs += parseFloat(meal.carbs) || 0;
            monthData.fats += parseFloat(meal.fats) || 0;
            monthData.fiber += parseFloat(meal.fiber) || 0;
            monthData.sugar += parseFloat(meal.sugar) || 0;
            monthData.sodium += parseFloat(meal.sodium) || 0;
            monthData.mealCount++;
          }
        }
      });
      
      // Convertir sumas en promedios para 3 meses
      months.forEach(month => {
        if (month.mealCount > 0) {
          month.calories = Math.round(month.calories / month.mealCount);
          month.protein = Math.round(month.protein / month.mealCount);
          month.carbs = Math.round(month.carbs / month.mealCount);
          month.fats = Math.round(month.fats / month.mealCount);
          month.fiber = Math.round(month.fiber / month.mealCount);
          month.sugar = Math.round(month.sugar / month.mealCount);
          month.sodium = Math.round(month.sodium / month.mealCount);
        }
      });
      
      return months;
    } else if (period === 'year' || period === '1Y') {
      // Para año, mostrar los últimos 12 meses
      const months = [];
      
      for (let i = 11; i >= 0; i--) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const nextMonth = new Date(today.getFullYear(), today.getMonth() - i + 1, 0);
        
        months.push({
          date: monthDate.toISOString().split('T')[0],
          endDate: nextMonth.toISOString().split('T')[0],
          label: monthDate.toLocaleDateString('en-US', { month: 'short' }),
          dayLabel: monthDate.toLocaleDateString('en-US', { month: 'long' }),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: i === 0,
          mealCount: 0
        });
      }
      
      // Asignar cada comida a su mes correspondiente
      meals.forEach(meal => {
        const mealDate = meal.mealDate || meal.date;
        if (mealDate) {
          const mealDateObj = new Date(mealDate);
          
          // Encontrar el mes al que pertenece esta comida
          const monthData = months.find(month => {
            const monthStart = new Date(month.date);
            const monthEnd = new Date(month.endDate);
            return mealDateObj >= monthStart && mealDateObj <= monthEnd;
          });
          
          if (monthData) {
            // Sumar los valores nutricionales
            monthData.calories += parseFloat(meal.calories) || 0;
            monthData.protein += parseFloat(meal.protein) || 0;
            monthData.carbs += parseFloat(meal.carbs) || 0;
            monthData.fats += parseFloat(meal.fats) || 0;
            monthData.fiber += parseFloat(meal.fiber) || 0;
            monthData.sugar += parseFloat(meal.sugar) || 0;
            monthData.sodium += parseFloat(meal.sodium) || 0;
            monthData.mealCount++;
          }
        }
      });
      
      // Convertir sumas en promedios para el año
      months.forEach(month => {
        if (month.mealCount > 0) {
          month.calories = Math.round(month.calories / month.mealCount);
          month.protein = Math.round(month.protein / month.mealCount);
          month.carbs = Math.round(month.carbs / month.mealCount);
          month.fats = Math.round(month.fats / month.mealCount);
          month.fiber = Math.round(month.fiber / month.mealCount);
          month.sugar = Math.round(month.sugar / month.mealCount);
          month.sodium = Math.round(month.sodium / month.mealCount);
        }
      });
      
      return months;
    } else if (period === 'max') {
      // Para máximo, mostrar datos por año
      // Obtener el año más antiguo y el más reciente
      let minYear = today.getFullYear();
      let maxYear = today.getFullYear();
      
      meals.forEach(meal => {
        const mealDate = meal.mealDate || meal.date;
        if (mealDate) {
          const mealYear = new Date(mealDate).getFullYear();
          minYear = Math.min(minYear, mealYear);
          maxYear = Math.max(maxYear, mealYear);
        }
      });
      
      // Crear array para cada año
      const years = [];
      for (let year = minYear; year <= maxYear; year++) {
        years.push({
          year,
          date: `${year}-01-01`,
          label: year.toString(),
          dayLabel: year.toString(),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: year === today.getFullYear(),
          mealCount: 0
        });
      }
      
      // Asignar cada comida a su año correspondiente
      meals.forEach(meal => {
        const mealDate = meal.mealDate || meal.date;
        if (mealDate) {
          const mealYear = new Date(mealDate).getFullYear();
          const yearData = years.find(y => y.year === mealYear);
          
          if (yearData) {
            // Sumar los valores nutricionales
            yearData.calories += parseFloat(meal.calories) || 0;
            yearData.protein += parseFloat(meal.protein) || 0;
            yearData.carbs += parseFloat(meal.carbs) || 0;
            yearData.fats += parseFloat(meal.fats) || 0;
            yearData.fiber += parseFloat(meal.fiber) || 0;
            yearData.sugar += parseFloat(meal.sugar) || 0;
            yearData.sodium += parseFloat(meal.sodium) || 0;
            yearData.mealCount++;
          }
        }
      });
      
      // Convertir sumas en promedios para máximo
      years.forEach(year => {
        if (year.mealCount > 0) {
          year.calories = Math.round(year.calories / year.mealCount);
          year.protein = Math.round(year.protein / year.mealCount);
          year.carbs = Math.round(year.carbs / year.mealCount);
          year.fats = Math.round(year.fats / year.mealCount);
          year.fiber = Math.round(year.fiber / year.mealCount);
          year.sugar = Math.round(year.sugar / year.mealCount);
          year.sodium = Math.round(year.sodium / year.mealCount);
        }
      });
      
      return years;
    }
    
    return [];
  };

  // Función para formatear datos de peso
  const formatWeightData = (data, period) => {
    if (!Array.isArray(data)) return [];
  
    // Transformar los datos para que tengan el formato esperado por Weight.js
    return data.map(item => ({
      checkInDate: item.checkInDate || item.date,
      weight: parseFloat(item.weight || item.value || 0),
      bodyFat: item.bodyFat, // Asegúrate de incluir bodyFat
      measurements: item.measurements, // Asegúrate de incluir measurements
      date: item.checkInDate || item.date,
      value: parseFloat(item.weight || item.value || 0),
      label: item.label || '',
      dayLabel: item.dayLabel || ''
    }));
  };
  
  // Función original para formatear datos de calorías (Unused function - removed for space)
  // const formatCaloriesData = (data, period) => { ... };
  
  // Función corregida para obtener etiquetas de día de la semana
  const getDayLabel = (dateString) => {
    if (!dateString) return '';
  
    try {
      const date = new Date(dateString);
  
      // Obtener el día de la semana (0 = domingo, 1 = lunes, etc.)
      const dayOfWeek = date.getDay();
  
      // Array con los nombres de los días de la semana en orden correcto
      const weekdays = [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
      // Devolver el nombre del día de la semana
      return weekdays[dayOfWeek];
    } catch (error) {
      console.error("Error formatting date:", error);
      return '';
    }
  };
  
  // Función para depurar fechas (Unused function)
  // const debugDate = (dateString) => {
  //   const date = new Date(dateString);
  //   return {
  //     originalString: dateString,
  //     dateObject: date,
  //     dayOfWeek: date.getDay(),
  //     dayOfWeekName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()],
  //     month: date.getMonth() + 1,
  //     day: date.getDate(),
  //     year: date.getFullYear(),
  //     isoString: date.toISOString(),
  //     localeDateString: date.toLocaleDateString()
  //   };
  // };
  
  // Generar datos de ejemplo para calorías
  const generateDefaultCaloriesData = (period = 'week') => {
    const data = [];
    const today = new Date();
    
    if (period === 'week' || period === '7D') {
      // Para semana, mostrar los últimos 7 días incluyendo hoy
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        data.push({
          date: dateStr,
          label: getDayLabel(dateStr),
          dayLabel: getDayLabel(dateStr),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: i === 0
        });
      }
    } else if (period === 'month' || period === '1M') {
      // Para mes, mostrar 4 semanas
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      for (let week = 1; week <= 4; week++) {
        const weekStart = new Date(currentYear, currentMonth, (week - 1) * 7 + 1);
        const isCurrentWeek = Math.ceil(today.getDate() / 7) === week;
        
        data.push({
          date: weekStart.toISOString().split('T')[0],
          label: `W${week}`,
          dayLabel: `Week ${week}`,
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: isCurrentWeek
        });
      }
    } else if (period === '3month' || period === '3M') {
      // Para 3 meses, mostrar los últimos 3 meses
      for (let i = 2; i >= 0; i--) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
        
        data.push({
          date: monthDate.toISOString().split('T')[0],
          label: monthDate.toLocaleDateString('en-US', { month: 'short' }),
          dayLabel: monthDate.toLocaleDateString('en-US', { month: 'long' }),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: i === 0
        });
      }
    } else {
      // Para año o máximo, mostrar los últimos 12 meses
      for (let i = 11; i >= 0; i--) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
        
        data.push({
          date: monthDate.toISOString().split('T')[0],
          label: monthDate.toLocaleDateString('en-US', { month: 'short' }),
          dayLabel: monthDate.toLocaleDateString('en-US', { month: 'long' }),
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
          fiber: 0,
          sugar: 0,
          sodium: 0,
          active: i === 0
        });
      }
    }
    
    return data;
  };
  
  // Generar datos de ejemplo para peso
  const generateDefaultWeightData = (period = 'week') => {
    return generateDefaultCaloriesData(period);
  };

  // Manejador para añadir check-in
  const handleAddCheckIn = async (checkInData) => {
    const response = await agtfitService.addCheckIn(checkInData);
    
    if (response.success) {
      // Actualizar la lista de check-ins
      setCheckIns(prevCheckIns => [response.data, ...prevCheckIns]);
      
      // Actualizar estadísticas de peso
      loadStats('weight', 'week');
      
      // Actualizar el objetivo con el nuevo peso
      fetchGoalData();
    } else {
      console.error('Error al guardar check-in:', response.error);
    }
  };

  // Manejador para actualizar check-in
  const handleUpdateCheckIn = async (checkInData) => {
    const response = await agtfitService.updateCheckIn(checkInData.checkInId, checkInData);
    
    if (response.success) {
      // Actualizar la lista de check-ins
      setCheckIns(prevCheckIns => 
        prevCheckIns.map(checkIn => 
          checkIn.checkInId === checkInData.checkInId ? response.data : checkIn
        )
      );
      
      // Actualizar estadísticas de peso
      loadStats('weight', 'week');
      
      // Actualizar el objetivo con el nuevo peso
      fetchGoalData();
    } else {
      console.error('Error al actualizar check-in:', response.error);
    }
  };

  // Manejador para eliminar check-in
  const handleDeleteCheckIn = async (checkInId) => {
    const response = await agtfitService.deleteCheckIn(checkInId);
    
    if (response.success) {
      // Actualizar la lista de check-ins
      setCheckIns(prevCheckIns => 
        prevCheckIns.filter(checkIn => checkIn.checkInId !== checkInId)
      );
      
      // Actualizar estadísticas de peso
      loadStats('weight', 'week');
      
      // Actualizar el objetivo con el nuevo peso
      fetchGoalData();
    } else {
      console.error('Error al eliminar check-in:', response.error);
    }
  };

  // Manejador para actualizar objetivo
  const handleUpdateGoal = async (goalDataToSave) => {
    const response = await agtfitService.updateGoal(goalDataToSave);
    
    if (response.success) {
      setGoalData(response.data);
      
      // Llamar al callback para actualizar el componente padre
      if (onGoalUpdated) {
        onGoalUpdated(response.data);
      }
      
      // Invalidar caché y actualizar las estadísticas
      sessionStorage.removeItem('stats_day_' + new Date().toISOString().split('T')[0]);
      loadStats('calories', 'week');
    } else {
      console.error('Error al guardar objetivo:', response.error);
    }
  };

  // Manejador para cambiar período de calorías
  const handleCaloriesPeriodChange = (period) => {
    debouncedLoadStats('calories', period);
  };

  // Manejador para cambiar período de peso
  const handleWeightPeriodChange = (period) => {
    debouncedLoadStats('weight', period);
  };

  return (
    <Box sx={{ padding: theme.spacing(3, 0) }}>
      {/* Componente de Check-Ins */}
      <CheckIns 
        checkIns={checkIns} 
        loading={loading.checkIns} 
        onAddCheckIn={handleAddCheckIn}
        onUpdateCheckIn={handleUpdateCheckIn}
        onDeleteCheckIn={handleDeleteCheckIn}
      />
  
      <SectionDivider />
  
      {/* Componente de Goals */}
      <Goals 
        goalData={goalData} 
        loading={loading.goal} 
        onUpdateGoal={handleUpdateGoal} 
      />
  
      <SectionDivider />
  
      {/* Componente de Calories */}
      <Calories 
        caloriesData={caloriesData} 
        loading={loading.caloriesStats} 
        goalData={goalData} 
        onPeriodChange={handleCaloriesPeriodChange} 
      />
  
      <SectionDivider />
  
      {/* Componente de Weight */}
      <Weight 
        weightData={weightData} 
        loading={loading.weightStats} 
        goalData={goalData} 
        onPeriodChange={handleWeightPeriodChange} 
      />
  
      {/* Botón flotante para añadir check-in */}
      <CheckInFab onClick={() => document.getElementById('add-check-in-button')?.click()}>
        <Plus />
      </CheckInFab>
    </Box>
  );
};

export default FitGoals;